import { axiosAssessmentInstance } from '../AxiosConfiguration';

/**
 * Base API client for admin endpoints
 * All admin API calls should go through this service
 */

/**
 * Get all sponsors with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.category - Filter by industry category
 * @param {boolean} params.active - Filter by active status
 * @param {number} params.page - Page number for pagination
 * @param {number} params.limit - Items per page
 * @returns {Promise<{sponsors: Array, total: number, page: number, totalPages: number}>}
 */
export const getSponsors = async (params = {}) => {
  try {
    const response = await axiosAssessmentInstance.get('/api/sponsorship/sponsors', { params });
    return response.data?.payload || {
      sponsors: [],
      total: 0,
      page: 1,
      totalPages: 0
    };
  } catch (error) {
    console.error('Error fetching sponsors:', error);
    throw error;
  }
};

/**
 * Create a new sponsor
 * @param {Object} sponsorData - Sponsor data
 * @returns {Promise<Object>} Created sponsor
 */
export const createSponsor = async (sponsorData) => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/sponsors', sponsorData);
    return response.data?.payload;
  } catch (error) {
    console.error('Error creating sponsor:', error);
    throw error;
  }
};

/**
 * Update an existing sponsor
 * @param {string} sponsorId - Sponsor ID
 * @param {Object} sponsorData - Updated sponsor data
 * @returns {Promise<Object>} Updated sponsor
 */
export const updateSponsor = async (sponsorId, sponsorData) => {
  try {
    const response = await axiosAssessmentInstance.patch(`/api/sponsorship/sponsors/${sponsorId}`, sponsorData);
    return response.data?.payload;
  } catch (error) {
    console.error('Error updating sponsor:', error);
    throw error;
  }
};

/**
 * Get sponsor by ID
 * @param {string} sponsorId - Sponsor ID
 * @returns {Promise<Object>} Sponsor details
 */
export const getSponsorById = async (sponsorId) => {
  try {
    const response = await axiosAssessmentInstance.get(`/api/sponsorship/sponsors/${sponsorId}`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching sponsor:', error);
    throw error;
  }
};

/**
 * Get all contracts with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status (draft, active, paused, completed)
 * @param {string} params.sponsor_id - Filter by sponsor ID
 * @param {number} params.page - Page number for pagination
 * @param {number} params.limit - Items per page
 * @returns {Promise<{contracts: Array, total: number, page: number, totalPages: number}>}
 */
export const getContracts = async (params = {}) => {
  try {
    const response = await axiosAssessmentInstance.get('/api/sponsorship/contracts', { params });
    return response.data?.payload || {
      contracts: [],
      total: 0,
      page: 1,
      totalPages: 0
    };
  } catch (error) {
    console.error('Error fetching contracts:', error);
    throw error;
  }
};

/**
 * Create a new contract
 * @param {Object} contractData - Contract data
 * @returns {Promise<Object>} Created contract
 */
export const createContract = async (contractData) => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/contracts', contractData);
    return response.data?.payload;
  } catch (error) {
    console.error('Error creating contract:', error);
    throw error;
  }
};

/**
 * Get contract by ID
 * @param {string} contractId - Contract ID
 * @returns {Promise<Object>} Contract details
 */
export const getContractById = async (contractId) => {
  try {
    const response = await axiosAssessmentInstance.get(`/api/sponsorship/contracts/${contractId}`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching contract:', error);
    throw error;
  }
};

/**
 * Update a contract (draft only)
 * @param {string} contractId - Contract ID
 * @param {Object} contractData - Updated contract data
 * @returns {Promise<Object>} Updated contract
 */
export const updateContract = async (contractId, contractData) => {
  try {
    const response = await axiosAssessmentInstance.patch(`/api/sponsorship/contracts/${contractId}`, contractData);
    return response.data?.payload;
  } catch (error) {
    console.error('Error updating contract:', error);
    throw error;
  }
};

/**
 * Run feasibility check for a contract
 * @param {string} contractId - Contract ID
 * @returns {Promise<Object>} Feasibility results
 */
export const runFeasibilityCheck = async (contractId) => {
  try {
    const response = await axiosAssessmentInstance.post(`/api/sponsorship/contracts/${contractId}/feasibility`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error running feasibility check:', error);
    throw error;
  }
};

/**
 * Activate a contract
 * @param {string} contractId - Contract ID
 * @returns {Promise<Object>} Updated contract
 */
export const activateContract = async (contractId) => {
  try {
    const response = await axiosAssessmentInstance.post(`/api/sponsorship/contracts/${contractId}/activate`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error activating contract:', error);
    throw error;
  }
};

/**
 * Pause a contract
 * @param {string} contractId - Contract ID
 * @returns {Promise<Object>} Updated contract
 */
export const pauseContract = async (contractId) => {
  try {
    const response = await axiosAssessmentInstance.post(`/api/sponsorship/contracts/${contractId}/pause`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error pausing contract:', error);
    throw error;
  }
};

/**
 * Resume a contract
 * @param {string} contractId - Contract ID
 * @returns {Promise<Object>} Updated contract
 */
export const resumeContract = async (contractId) => {
  try {
    const response = await axiosAssessmentInstance.post(`/api/sponsorship/contracts/${contractId}/resume`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error resuming contract:', error);
    throw error;
  }
};

/**
 * Complete a contract
 * @param {string} contractId - Contract ID
 * @returns {Promise<Object>} Updated contract
 */
export const completeContract = async (contractId) => {
  try {
    const response = await axiosAssessmentInstance.post(`/api/sponsorship/contracts/${contractId}/complete`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error completing contract:', error);
    throw error;
  }
};

/**
 * Get industry categories
 * @returns {Promise<Array>} List of industry categories
 */
export const getCategories = async () => {
  try {
    const response = await axiosAssessmentInstance.get('/api/sponsorship/categories');
    return response.data?.payload || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Get category by ID
 * @param {string} categoryId - Category ID
 * @returns {Promise<Object>} Category details
 */
export const getCategoryById = async (categoryId) => {
  try {
    const response = await axiosAssessmentInstance.get(`/api/sponsorship/categories/${categoryId}`);
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching category:', error);
    throw error;
  }
};

/**
 * Create industry category
 * @param {Object} categoryData - Category data
 * @returns {Promise<Object>} Created category
 */
export const createCategory = async (categoryData) => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/categories', categoryData);
    return response.data?.payload;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

/**
 * Update industry category
 * @param {string} categoryId - Category ID
 * @param {Object} categoryData - Updated category data
 * @returns {Promise<Object>} Updated category
 */
export const updateCategory = async (categoryId, categoryData) => {
  try {
    const response = await axiosAssessmentInstance.patch(`/api/sponsorship/categories/${categoryId}`, categoryData);
    return response.data?.payload;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

/**
 * Query inventory
 * @param {Object} params - Query parameters
 * @param {string} params.scope - Coverage scope (national, territory)
 * @param {Array} params.territories - List of territories
 * @param {string} params.start_date - Start date filter
 * @param {string} params.end_date - End date filter
 * @returns {Promise<Object>} Inventory query results
 */
export const queryInventory = async (params = {}) => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/inventory/query', params);
    return response.data?.payload || {};
  } catch (error) {
    console.error('Error querying inventory:', error);
    throw error;
  }
};

/**
 * Get sponsor delivery report
 * @param {string} sponsorId - Sponsor ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Delivery report data
 */
export const getSponsorDeliveryReport = async (sponsorId, params = {}) => {
  try {
    const response = await axiosAssessmentInstance.get(`/api/sponsorship/reports/sponsor-delivery/${sponsorId}`, { params });
    return response.data?.payload || {};
  } catch (error) {
    console.error('Error fetching sponsor delivery report:', error);
    throw error;
  }
};

/**
 * Get unfilled slots report
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Unfilled slots data
 */
export const getUnfilledSlotsReport = async (params = {}) => {
  try {
    const response = await axiosAssessmentInstance.get('/api/sponsorship/reports/unfilled-slots', { params });
    return response.data?.payload || {};
  } catch (error) {
    console.error('Error fetching unfilled slots report:', error);
    throw error;
  }
};

/**
 * Run weekly assignment job
 * @returns {Promise<Object>} Job execution results
 */
export const runWeeklyAssignmentJob = async () => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/assignments/run-weekly-job');
    return response.data?.payload || {};
  } catch (error) {
    console.error('Error running weekly assignment job:', error);
    throw error;
  }
};

/**
 * Upload media file
 * @param {FormData} formData - File data
 * @returns {Promise<Object>} Upload result with file URL
 */
export const uploadMedia = async (formData) => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data?.payload;
  } catch (error) {
    console.error('Error uploading media:', error);
    throw error;
  }
};

/**
 * Get dashboard overview with key metrics
 * @returns {Promise<Object>} Dashboard overview data
 */
export const getDashboardOverview = async () => {
  try {
    const response = await axiosAssessmentInstance.get('/api/sponsorship/dashboard/overview');
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching dashboard overview:', error);
    throw error;
  }
};

/**
 * Get weekly delivery report for a sponsor
 * @param {string} sponsorId - Sponsor ID
 * @param {number} year - Calendar year
 * @param {number} week - Calendar week (1-52)
 * @returns {Promise<Object>} Weekly delivery report data
 */
export const getWeeklyDeliveryReport = async (sponsorId, year, week) => {
  try {
    const response = await axiosAssessmentInstance.get(
      `/api/sponsorship/reports/weekly-delivery/${sponsorId}?year=${year}&week=${week}`
    );
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching weekly delivery report:', error);
    throw error;
  }
};

/**
 * Get contract validation report
 * @param {string} contractId - Contract ID
 * @param {boolean} includeWeeklyBreakdown - Include weekly breakdown data
 * @returns {Promise<Object>} Contract validation report data
 */
export const getContractValidationReport = async (contractId, includeWeeklyBreakdown = false) => {
  try {
    const response = await axiosAssessmentInstance.get(
      `/api/sponsorship/reports/contract-validation/${contractId}?include_weekly_breakdown=${includeWeeklyBreakdown}`
    );
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching contract validation report:', error);
    throw error;
  }
};

/**
 * Get sponsor summary report
 * @param {string} sponsorId - Sponsor ID
 * @param {string} startDate - Start date (YYYY-MM-DD) - optional
 * @param {string} endDate - End date (YYYY-MM-DD) - optional
 * @returns {Promise<Object>} Sponsor summary report data
 */
export const getSponsorSummaryReport = async (sponsorId, startDate, endDate) => {
  try {
    let url = `/api/sponsorship/reports/sponsor-summary/${sponsorId}`;
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await axiosAssessmentInstance.get(url);
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching sponsor summary report:', error);
    throw error;
  }
};

/**
 * Get inventory utilization report
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Inventory utilization report data
 */
export const getInventoryUtilizationReport = async (params) => {
  try {
    const response = await axiosAssessmentInstance.post('/api/sponsorship/reports/inventory-utilization', params);
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching inventory utilization report:', error);
    throw error;
  }
};

/**
 * Get usage signals report (optional)
 * @param {string} sponsorId - Sponsor ID
 * @param {number} year - Calendar year
 * @param {number} week - Calendar week (1-52)
 * @returns {Promise<Object>} Usage signals report data
 */
export const getUsageSignalReport = async (sponsorId, year, week) => {
  try {
    const response = await axiosAssessmentInstance.get(
      `/api/sponsorship/reports/usage-signals/${sponsorId}?year=${year}&week=${week}`
    );
    return response.data?.payload;
  } catch (error) {
    console.error('Error fetching usage signal report:', error);
    throw error;
  }
};
